/**
 * One dialog for the four acts on a client's ledger (T-C3 Doładowanie, T-C4 Rozliczenie
 * zaległości, T-C5 Korekta, T-C6 Anuluj obciążenie).
 *
 * The four are one shape — an amount (or, for a forgiveness, the session it names), a note the
 * client will read, the balance after, one ember act — and what each may carry is
 * `model/ledgerAct`'s verdict, not this file's. The amount is Base UI's number field: − and +
 * buttons, the arrow keys, typing; the model clamps it into the act's range. A forgiveness picks
 * its session from the charges that may still be taken back (a radio group of the canon's rows).
 * „Zapisz" stays focusable while it cannot send and says why beside it; a refusal keeps the dialog
 * open and says why above the acts. The dialog opens on its first field.
 */

import { Dialog } from '@base-ui/react/dialog';
import { NumberField } from '@base-ui/react/number-field';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import choice from '~/shared/ui/choiceRow.module.css';
import surface from '~/shared/ui/dialogSurface.module.css';
import form from '~/shared/ui/formDialog.module.css';
import { CloseGlyph } from '~/shared/ui/FormGlyphs';
import hiddenControl from '~/shared/ui/visuallyHiddenControl.module.css';

import {
  LEDGER_NOTE_MAX_LENGTH,
  MAX_LEDGER_AMOUNT,
  ledgerActVerdict,
  withAmount,
  withNote,
  withSession,
  type LedgerActDraft,
  type LedgerField,
  type LedgerStanding,
} from '../model/ledgerAct';
import { creditStanding, standingPhrase } from '../model/standing';
import styles from './LedgerActDialog.module.css';

interface LedgerActDialogProps {
  readonly draft: LedgerActDraft | null;
  readonly clientName: string;
  readonly standing: LedgerStanding;
  /**
   * What a forgiveness picks from — only for a host that opens one. The client card offers the
   * grant and the settlement alone, and passes nothing.
   */
  readonly forgiveness?: LedgerForgiveness | undefined;
  readonly sending: boolean;
  /**
   * The last send got no answer. The fields lock — the draft that went out is the draft — and the
   * act goes again under the same request id („Wyślij ponownie"), or the dialog is closed.
   */
  readonly locked: boolean;
  readonly refusal: { readonly text: string; readonly field: LedgerField | null } | null;
  readonly onEdit: (draft: LedgerActDraft) => void;
  readonly onSend: () => void;
  readonly onClose: () => void;
}

/** One session a forgiveness may name: „wt 22.09, 9:00" over „Trening · nieobecność". */
export interface LedgerForgiveness {
  /** The charges a forgiveness may name, already worded — `null` while they are read. */
  readonly picks: readonly LedgerPick[] | null;
  readonly failed: boolean;
  readonly onRetry: () => void;
}

export interface LedgerPick {
  readonly id: number;
  readonly when: string;
  readonly what: string;
}

const SHEET = 'web:booking.trainer.credits.sheet';

export function LedgerActDialog({
  draft,
  clientName,
  standing,
  forgiveness,
  sending,
  locked,
  refusal,
  onEdit,
  onSend,
  onClose,
}: LedgerActDialogProps) {
  const { t } = useTranslation();
  const forgivable = forgiveness === undefined ? [] : forgiveness.picks;
  const forgivableFailed = forgiveness?.failed === true;
  const amountId = useId();
  const noteId = useId();
  const reasonId = useId();
  const refusalId = useId();
  const pickName = useId();
  const firstField = useRef<HTMLElement | null>(null);
  // A required note is not wrong while the trainer has not got to it: it is marked once they leave
  // it empty, or once they press the act. The reason beside the act says it from the start.
  const [attempted, setAttempted] = useState(false);
  const [noteLeft, setNoteLeft] = useState(false);
  // Both belong to one act: a new dialog starts unmarked.
  const [markedFor, setMarkedFor] = useState<string | null>(null);
  if (draft !== null && markedFor !== draft.requestId) {
    setMarkedFor(draft.requestId);
    setAttempted(false);
    setNoteLeft(false);
  }

  const verdict = draft === null ? null : ledgerActVerdict(draft, standing);
  const owed = Math.max(0, standing.sessionsOwed);
  const reason =
    verdict === null
      ? null
      : locked
        ? null
        : verdict.blockedBy === null
          ? null
          : verdict.blockedBy === 'nothingOwed'
            ? t(`${SHEET}.settlement.nothingOwed`)
            : verdict.blockedBy === 'zero'
              ? t('web:booking.credits.act.zeroAdjustment')
              : verdict.blockedBy === 'amount'
                ? t('web:booking.credits.act.amountTooFar', { max: MAX_LEDGER_AMOUNT })
                : verdict.blockedBy === 'note'
                  ? t('web:booking.credits.act.noteNeeded')
                  : t('web:booking.credits.act.sessionNeeded');
  const amountWrong =
    verdict !== null && (verdict.blockedBy === 'zero' || verdict.blockedBy === 'amount');
  const noteWrong = verdict !== null && verdict.blockedBy === 'note' && (attempted || noteLeft);
  /** A field's marking: the server's refusal of it first, then the dialog's own reason. */
  const marking = (field: LedgerField, wrong: boolean) =>
    refusal?.field === field
      ? { invalid: true as const, describedBy: refusalId }
      : wrong
        ? { invalid: true as const, describedBy: reasonId }
        : { invalid: undefined, describedBy: undefined };
  const amountMark = marking('amount', amountWrong);
  const noteMark = marking('note', noteWrong);
  const before = standingPhrase(creditStanding(standing), t);

  return (
    <Dialog.Root
      open={draft !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className={surface.backdrop} />
        <Dialog.Popup
          className={`${String(surface.popup)} ${String(form.dialog)}`}
          initialFocus={firstField}
        >
          {draft === null || verdict === null ? null : (
            <>
              <div className={form.dialogHead}>
                <div>
                  <span className={form.eyebrow}>
                    {t('web:booking.credits.act.eyebrow', {
                      name: clientName,
                      act: t(`${SHEET}.${draft.act}.eyebrow`),
                    })}
                  </span>
                  <Dialog.Title className={form.dialogTitle}>
                    {t(`${SHEET}.${draft.act}.title`)}
                  </Dialog.Title>
                </div>
                <Dialog.Close className={form.close} aria-label={t(`${SHEET}.close`)}>
                  <CloseGlyph className={form.closeGlyph} />
                </Dialog.Close>
              </div>

              {draft.act === 'forgiveness' ? (
                <fieldset
                  className={`${String(form.field)} ${String(styles.pickSet)}`}
                  aria-invalid={refusal?.field === 'calendarEventId' ? true : undefined}
                  aria-describedby={
                    refusal?.field === 'calendarEventId'
                      ? refusalId
                      : verdict.blockedBy === 'session' && attempted
                        ? reasonId
                        : undefined
                  }
                  onKeyDown={(event) => {
                    // Native radios walk with the arrows; Home and End reach the list's ends.
                    if (forgivable === null || forgivable.length === 0) return;
                    const target =
                      event.key === 'Home'
                        ? forgivable[0]
                        : event.key === 'End'
                          ? forgivable[forgivable.length - 1]
                          : undefined;
                    if (target === undefined) return;
                    event.preventDefault();
                    onEdit(withSession(draft, target.id));
                    event.currentTarget
                      .querySelector<HTMLInputElement>(`input[value="${String(target.id)}"]`)
                      ?.focus();
                  }}
                >
                  <legend className={form.fieldLabel}>{t(`${SHEET}.forgiveness.pick`)}</legend>
                  {/* One reserved box for the list's three other states, so none of them moves
                      the note and the acts under it. */}
                  {forgivable === null || forgivable.length === 0 ? (
                    <div className={styles.pickState} role="status">
                      {forgivable === null && forgivableFailed ? (
                        <>
                          <span>{t(`${SHEET}.forgiveness.errorTitle`)}</span>
                          <button
                            ref={(node) => {
                              firstField.current = node;
                            }}
                            type="button"
                            className={surface.ghost}
                            onClick={forgiveness?.onRetry}
                          >
                            {t(`${SHEET}.forgiveness.errorRetry`)}
                          </button>
                        </>
                      ) : forgivable === null ? (
                        <span>{t('web:booking.credits.act.loadingSessions')}</span>
                      ) : (
                        <span>{t(`${SHEET}.forgiveness.empty`)}</span>
                      )}
                    </div>
                  ) : (
                    <div className={choice.rows}>
                      {forgivable.map((session, index) => {
                        const chosen = draft.calendarEventId === session.id;
                        return (
                          <label
                            key={session.id}
                            className={`${String(choice.row)} ${chosen ? String(choice.rowChosenInk) : ''}`}
                          >
                            <input
                              ref={
                                chosen || (draft.calendarEventId === null && index === 0)
                                  ? (node) => {
                                      firstField.current = node;
                                    }
                                  : undefined
                              }
                              className={hiddenControl.control}
                              type="radio"
                              name={pickName}
                              value={session.id}
                              checked={chosen}
                              disabled={locked}
                              onChange={() => {
                                onEdit(withSession(draft, session.id));
                              }}
                            />
                            <span className={choice.rowMark} aria-hidden="true" />
                            <span className={choice.rowText}>
                              <span className={choice.rowTitle}>{session.when}</span>
                              <span className={choice.rowMeta}>{session.what}</span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  <p className={form.note}>{t('web:booking.credits.act.forgivenessWindow')}</p>
                </fieldset>
              ) : verdict.range === null ? null : (
                <div className={form.field}>
                  <label className={form.fieldLabel} htmlFor={amountId}>
                    {draft.act === 'adjustment'
                      ? t('web:booking.credits.act.adjustmentLabel')
                      : draft.act === 'settlement'
                        ? t('web:booking.credits.act.sessionsLabel')
                        : t('web:booking.credits.act.creditsLabel')}
                  </label>
                  <NumberField.Root
                    id={amountId}
                    value={draft.amount}
                    min={verdict.range.min}
                    max={verdict.range.max}
                    step={1}
                    allowWheelScrub={false}
                    disabled={locked}
                    onValueChange={(value) => {
                      if (value !== null) onEdit(withAmount(draft, value, owed));
                    }}
                  >
                    <NumberField.Group className={styles.stepper}>
                      <NumberField.Decrement
                        className={styles.stepperButton}
                        aria-label={t(`${SHEET}.less`)}
                      >
                        −
                      </NumberField.Decrement>
                      <NumberField.Input
                        ref={(node) => {
                          firstField.current = node;
                        }}
                        className={styles.stepperValue}
                        aria-invalid={amountMark.invalid}
                        aria-describedby={amountMark.describedBy}
                      />
                      <NumberField.Increment
                        className={styles.stepperButton}
                        aria-label={t(`${SHEET}.more`)}
                      >
                        +
                      </NumberField.Increment>
                      {draft.act === 'adjustment' ? (
                        <span className={styles.stepperHint}>
                          {t('web:booking.credits.act.adjustmentHint')}
                        </span>
                      ) : null}
                    </NumberField.Group>
                  </NumberField.Root>
                </div>
              )}

              <div className={form.field}>
                <label className={form.fieldLabel} htmlFor={noteId}>
                  {`${t(verdict.noteRequired ? `${SHEET}.noteRequired` : `${SHEET}.noteOptional`)} · ${t(`${SHEET}.noteHint`, { name: clientName })}`}
                </label>
                <textarea
                  id={noteId}
                  className={`${String(form.input)} ${String(styles.note)}`}
                  value={draft.note}
                  maxLength={LEDGER_NOTE_MAX_LENGTH}
                  placeholder={t(`${SHEET}.notePlaceholder`)}
                  readOnly={locked}
                  aria-invalid={noteMark.invalid}
                  aria-describedby={noteMark.describedBy}
                  onBlur={() => {
                    setNoteLeft(true);
                  }}
                  onChange={(event) => {
                    onEdit(withNote(draft, event.target.value));
                  }}
                />
              </div>

              {verdict.after === null ? null : (
                <p className={styles.after}>
                  <span>{t(`${SHEET}.balanceAfter`)}</span>
                  <b className={styles.afterFigure}>
                    {t('web:booking.ledger.projection', {
                      before,
                      after: standingPhrase(verdict.after, t),
                    })}
                  </b>
                </p>
              )}

              {draft.act === 'adjustment' ? (
                <p className={form.note}>{t('web:booking.credits.act.adjustmentFoot')}</p>
              ) : null}

              {refusal === null ? null : (
                <p id={refusalId} className={form.refusal} role="alert">
                  {refusal.text}
                </p>
              )}

              <div className={form.dialogActs}>
                <button type="button" className={surface.ghost} onClick={onClose}>
                  {locked ? t(`${SHEET}.close`) : t('web:booking.credits.act.cancel')}
                </button>
                {reason === null ? null : (
                  <span id={reasonId} className={form.actReason}>
                    {reason}
                  </span>
                )}
                <button
                  type="button"
                  className={surface.primary}
                  aria-disabled={reason !== null || sending ? true : undefined}
                  aria-describedby={reason === null ? undefined : reasonId}
                  aria-busy={sending}
                  onClick={() => {
                    setAttempted(true);
                    onSend();
                  }}
                >
                  {sending
                    ? t(`${SHEET}.busy`)
                    : locked
                      ? t('web:booking.credits.act.resend')
                      : draft.act === 'grant' || draft.act === 'settlement'
                        ? t(`${SHEET}.${draft.act}.cta`, { count: Math.abs(draft.amount) })
                        : t(`${SHEET}.${draft.act}.cta`)}
                </button>
              </div>
            </>
          )}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
